import React, { useState } from "react";

export const Credentials = () => {
  // Publications array with markdown-like syntax
  const publications = [
    "Abel, M. R., Henin, A., Holmén, J., **Kagan, E. R.**, Hamilton, A., Noyola, N., & Hirshfeld-Becker, D. R. (2023). Anxiety and Disruptive Behavior Symptoms and Disorders in Preschool-Age Offspring of Parents With and Without Bipolar Disorder: Associations With Parental Comorbidity. *Journal of Attention Disorders*.",
    "**Kagan, E. R.**, Frank, H.E., Palitz, S. A., & Kendall, P.C. (2023). Targeting Accommodation in the Treatment of Youth with Anxiety: An Open Trial of the Coping Cat Accommodation Reduction Intervention. *Journal of Child and Family Studies*.",
    "Philips, K.E., Palitz, S. A., Frank, H.E., **Kagan, E.R.**, Benito, K., & Kendall, P.C. (2022). Adapting the Pediatric Accommodation Scale; reliability and validity of a therapist report measure.",
    "Frank, H. E., **Kagan, E. R.**, Storch, E.A., Wood, J. J., Kerns, C., Lewin, A. B., Small, B. J., & Kendall, P. C. (2022). Accommodation of anxiety in youth with Autism Spectrum Disorder: Results from the Treatment of Anxiety in Autism Spectrum Disorder (TAASD) study. *Journal of Child and Adolescent Psychology*.",
    "**Kagan, E. R.**, Frank, H. E., Knepley, M. J., & Kendall, P. C. (2021). Beyond 16 Sessions: Extending Manualized Treatment for Anxious Youth. *Journal of Child and Family Studies*.",
    "**Kagan, E.R.**, Frank, H.E., Norris, L.A., Palitz, S. A., Chiappini, E. A., Knepley, M.J., Crane, M.E., Philips, K.E., Ginsburg, G.S., Keeton, C., Albano, A. M., Piacentini, J., Peris, T., Compton, S., Sakolosky, D., Birmaher, B., & Kendall, P.C. (2021). Antidepressant Use in a 3- to 12-Year Follow-up of Anxious Youth: Results from the CAMELS Trial. *Psychiatric Services*.",
    "Frank, H.E., Titone, M.K., **Kagan, E.R.**, Alloy, L.B., & Kendall, P.C. (2020). The role of comorbid depression in youth anxiety treatment outcomes. *Child Psychiatry and Human Development*.",
    "**Kagan, E.R.**, Frank, H. E., & Kendall, P.C. (2018). Accommodation in Youth with Mental Health Issues: Evidence and Issues. *Current Directions in Psychological Science*, 27(4), 227-231.",
    "Palitz, S., Carper, M. M., **Kagan, E. R.**, Aggarwal, R., Frank, H.E., Davis, J., & Kendall, P.C. (2018). Addressing Comorbidity When Treating Anxious Youth. *Evidence-Based Practice in Child and Adolescent Mental Health*, 3(1), 1-15.",
    "**Kagan, E. R.**, Frank, H. E., & Kendall, P. C. (2017). Accommodation in youth with OCD and anxiety. *Clinical Psychology: Science and Practice*, 24(1), 78–98.",
    "**Kagan, E. R.**, Peterman, J. S., Carper, M. M., & Kendall, P. C. (2016). Accommodation and treatment of anxious youth. *Depression and Anxiety*, 33(9), 840-847.",
    "Swan, A. J., **Kagan, E. R.**, Frank, H. E., Crawford, E., & Kendall, P. C. (2016). Collateral support: involving parents and schools in treatment for youth anxiety. *Evidence-Based Practice in Child and Adolescent Mental Health*, 1(1), 3-15.",
    "Kerns, C.M., Wood, J.J., Kendall, P.C., Renno, P., Crawford, E.A., Mercado, R.M., Fujii, C., Collier, A., Hoff, A., **Kagan, E.R.**, Small, B.J., Lewin, A.B., & Storch, E.A. (2016). The treatment of anxiety in autism spectrum disorder (TAASD) study: rationale, design, and methods. *Journal of Child and Family Studies*, 25(6), 1889-1902.",
    "Kendall, P.C., Makover, H., Swan, A., Carper, M., Mercado, R., **Kagan, E.**, & Crawford, E. (2016). What steps to take? Addressing the emergence of concerning anxiety in youth. *Clinical Psychology Review*, 23(3), 211-229.",
  ];

  // State to manage the expanded state
  const [showAll, setShowAll] = useState(false);

  // Determine the publications to display
  const publicationsToShow = showAll ? publications : publications.slice(0, 5);

  // Updated parsePublicationText function
  const parsePublicationText = (text) => {
    // Replace **text** with <strong>text</strong>
    text = text.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    // Replace *text* with <em>text</em>, avoiding conflicts with bold text
    text = text.replace(
      /(^|[^*])\*(?!\*)(.+?)\*(?!\*)([^*]|$)/g,
      "$1<em>$2</em>$3"
    );
    return text;
  };

  return (
    <div id="credentials" className="text-center">
      <div className="container">
        {/* About Section */}
        <div className="about-section">
          <div className="section-title">
            <h2>Dr. Elana Kagan</h2>
          </div>
          <div className="about-content">
            <p>
              As a clinical psychologist specializing in treating children and
              young adults with anxiety and OCD, I bring a wealth of academic
              and practical experience to my work. My journey began at Yale
              University, where I earned my BA in Psychology <em>cum laude</em>.
              I continued my education at Temple University, where I trained at
              one of the nation's premier anxiety treatment centers under Dr.
              Philip Kendall, a leading authority in child anxiety treatment. My
              doctoral research at Temple focused on developing more effective
              family-based interventions for childhood anxiety disorders, which
              I incorporate into my work. My clinical training included a
              postdoctoral fellowship at Kennedy Krieger Institute at Johns
              Hopkins School of Medicine, where I gained additional experience
              in treating children and teenagers with a wide range of concerns.
              This specialized training equipped me with proven strategies that
              I now use to help children and families overcome a wide range of
              challenges.
            </p>
            <p>
              After completing my training, I served as a clinical psychologist
              at Massachusetts General Hospital's Child CBT Clinic, where I
              provided evidence-based treatment for children and young adults
              with various behavioral and mental health challenges. I also
              worked within the Gender Identity Program, supporting transgender
              and gender diverse youth and their families through affirming
              care. I also previously worked as an instructor and clinical
              supervisor at Harvard Medical School, helping to train the next
              generation of psychiatrists through clinical supervision and
              mentorship. I now work as a psychologist at the Center for Anxiety
              and Behavior Therapy, providing evidence-based care for children,
              teenagers, and adults with anxiety.
            </p>
          </div>
        </div>

        {/* Credentials and Training Section */}
        <div className="credentials-section">
          <h2>Education & Clinical Training</h2>
          <div className="credentials-grid">
            <div className="credential-item">
              <strong>Yale University</strong>
              <p>
                BA in Psychology, <em>cum laude</em>
              </p>
            </div>
            <div className="credential-item">
              <strong>Temple University</strong>
              <p>PhD in Clinical Psychology</p>
              <p>Child & Adolescent Anxiety Disorders Clinic</p>
            </div>
            <div className="credential-item">
              <strong>Johns Hopkins School of Medicine</strong>
              <p>
                Clinical Internship and Post-Doctoral Fellowship at Kennedy
                Krieger Institute
              </p>
            </div>
            <div className="credential-item">
              <strong>Massachusetts General Hospital</strong>
              <p>Clinical Psychologist in the Child CBT Clinic</p>
            </div>
            <div className="credential-item">
              <strong>Harvard Medical School</strong>
              <p>Instructor and Clinical Supervisor</p>
            </div>
            <div className="credential-item">
              <strong>Gender Identity Program</strong>
              <p>
                Specialist in affirming care for transgender and gender diverse
                youth
              </p>
            </div>
          </div>
        </div>

        {/* Publications Section */}
        <div className="publications-section">
          <h2>Selected Publications</h2>
          <div className="publications-grid">
            {publicationsToShow.map((publication, index) => (
              <div className="publication-card" key={index}>
                <span
                  dangerouslySetInnerHTML={{
                    __html: parsePublicationText(publication),
                  }}
                />
              </div>
            ))}
          </div>
          {publications.length > 5 && (
            <button
              className="btn btn-custom"
              onClick={() => setShowAll(!showAll)}
            >
              {showAll ? "Show Less" : "View More Publications"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
